import React, { useEffect, useRef, useState } from "react";

export function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-2xl border border-line bg-panel/70 backdrop-blur p-5 shadow-lg shadow-black/10 ${className}`}
    >
      {children}
    </div>
  );
}

export function CollapsibleSection({
  title,
  children,
  defaultOpen = false,
  className = "",
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-2xl border border-line ${className}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-fg transition hover:bg-panel2/40"
      >
        <span>{title}</span>
        <svg
          viewBox="0 0 24 24"
          className={`h-4 w-4 shrink-0 text-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && <div className="border-t border-line px-4 pb-4 pt-3">{children}</div>}
    </div>
  );
}

/**
 * Mascota de la web: silueta de Westy (la misma del favicon, public/westie-mark-white.svg)
 * dentro de un cuadrado de esquinas redondeadas. El fondo usa `bg-accent`, así que cambia
 * de color según la tab activa (el acento se inyecta como --accent en el contenedor).
 */
export function WestyMark({ className = "" }: { className?: string }) {
  return (
    <span className={`inline-flex shrink-0 overflow-hidden rounded-[28%] bg-accent ${className}`}>
      <svg viewBox="0 0 1254 1254" className="h-full w-full" fill="#ffffff" aria-hidden>
        <path fillRule="evenodd" d="M 319 119 L 317 121 L 316 121 L 309 129 L 301 144 L 301 146 L 298 151 L 297 156 L 293 164 L 292 169 L 290 172 L 290 175 L 289 176 L 289 178 L 288 179 L 288 181 L 287 182 L 287 184 L 286 185 L 286 187 L 285 188 L 285 190 L 282 197 L 282 200 L 280 204 L 278 215 L 276 219 L 276 222 L 275 223 L 275 226 L 274 227 L 274 231 L 273 232 L 273 235 L 272 236 L 272 240 L 271 241 L 271 244 L 270 245 L 270 249 L 269 250 L 269 253 L 268 254 L 268 258 L 267 259 L 267 263 L 266 264 L 265 273 L 264 274 L 264 279 L 263 280 L 263 285 L 262 286 L 262 290 L 261 291 L 260 303 L 259 304 L 259 309 L 258 310 L 258 315 L 257 316 L 257 323 L 256 324 L 256 330 L 255 331 L 255 337 L 254 338 L 254 346 L 253 347 L 253 355 L 252 356 L 251 380 L 250 381 L 250 392 L 249 393 L 246 393 L 243 391 L 240 391 L 236 389 L 231 389 L 230 388 L 212 388 L 212 389 L 222 400 L 222 401 L 236 418 L 260 456 L 260 458 L 253 465 L 253 466 L 241 480 L 239 484 L 236 487 L 236 488 L 230 496 L 229 499 L 224 506 L 222 511 L 220 513 L 213 527 L 213 529 L 210 534 L 210 536 L 206 544 L 204 552 L 202 555 L 202 558 L 200 562 L 200 565 L 199 566 L 199 569 L 198 570 L 198 573 L 197 574 L 197 578 L 196 579 L 196 583 L 195 584 L 195 589 L 194 590 L 194 596 L 193 597 L 193 629 L 194 629 L 194 628 L 203 619 L 203 618 L 218 604 L 219 604 L 222 601 L 223 601 L 226 598 L 227 598 L 237 591 L 240 591 L 241 592 L 240 596 L 238 598 L 236 603 L 234 605 L 232 610 L 230 612 L 217 638 L 217 640 L 214 645 L 214 647 L 212 650 L 212 652 L 208 660 L 208 662 L 207 663 L 207 665 L 206 666 L 206 668 L 205 669 L 205 671 L 204 672 L 204 674 L 203 675 L 203 677 L 200 684 L 200 687 L 198 691 L 198 694 L 197 695 L 197 699 L 196 700 L 195 708 L 194 709 L 194 714 L 193 715 L 193 720 L 192 721 L 192 727 L 191 728 L 191 739 L 190 740 L 190 761 L 191 762 L 191 771 L 192 772 L 192 778 L 193 779 L 193 784 L 194 785 L 195 792 L 196 793 L 197 798 L 199 799 L 206 785 L 213 776 L 215 772 L 218 769 L 218 768 L 239 747 L 240 747 L 244 743 L 245 743 L 254 736 L 256 736 L 257 737 L 257 739 L 249 750 L 243 762 L 241 764 L 233 780 L 233 782 L 230 787 L 230 789 L 228 792 L 228 794 L 227 795 L 226 800 L 224 803 L 224 806 L 221 813 L 221 816 L 219 820 L 219 824 L 218 825 L 218 828 L 217 829 L 217 834 L 216 835 L 216 840 L 215 841 L 215 849 L 214 850 L 214 880 L 215 881 L 215 890 L 216 891 L 216 896 L 217 897 L 217 900 L 218 901 L 218 905 L 220 909 L 220 912 L 221 913 L 222 918 L 224 921 L 225 926 L 236 949 L 238 951 L 242 958 L 245 961 L 246 961 L 247 950 L 248 949 L 249 942 L 250 941 L 253 930 L 255 927 L 255 925 L 257 923 L 260 915 L 262 913 L 263 910 L 265 908 L 267 904 L 276 894 L 278 894 L 279 895 L 279 899 L 278 900 L 277 910 L 276 911 L 276 922 L 275 923 L 275 938 L 276 939 L 276 952 L 277 953 L 278 964 L 279 965 L 281 976 L 282 977 L 285 988 L 289 996 L 289 998 L 294 1008 L 296 1010 L 298 1015 L 300 1017 L 302 1021 L 305 1024 L 307 1028 L 314 1036 L 314 1037 L 334 1057 L 335 1057 L 343 1064 L 346 1065 L 345 1063 L 345 1053 L 344 1052 L 345 1036 L 346 1035 L 346 1030 L 347 1029 L 347 1026 L 348 1025 L 348 1022 L 349 1021 L 349 1018 L 350 1017 L 351 1012 L 352 1011 L 354 1011 L 356 1013 L 357 1019 L 358 1020 L 358 1023 L 359 1024 L 361 1032 L 364 1037 L 364 1039 L 369 1049 L 371 1051 L 372 1054 L 374 1056 L 378 1063 L 381 1066 L 381 1067 L 391 1078 L 391 1079 L 399 1087 L 400 1087 L 406 1093 L 407 1093 L 419 1103 L 420 1103 L 428 1109 L 433 1111 L 435 1113 L 451 1121 L 453 1121 L 461 1125 L 463 1125 L 470 1128 L 477 1129 L 478 1130 L 487 1131 L 488 1132 L 490 1132 L 489 1128 L 488 1127 L 488 1124 L 487 1123 L 487 1107 L 488 1106 L 488 1102 L 489 1101 L 491 1093 L 496 1083 L 501 1076 L 503 1076 L 504 1077 L 509 1087 L 519 1098 L 520 1098 L 525 1103 L 526 1103 L 537 1111 L 544 1114 L 546 1116 L 550 1118 L 552 1118 L 557 1121 L 559 1121 L 562 1123 L 564 1123 L 565 1124 L 567 1124 L 574 1127 L 577 1127 L 578 1128 L 581 1128 L 582 1129 L 586 1129 L 587 1130 L 591 1130 L 592 1131 L 596 1131 L 597 1132 L 603 1132 L 604 1133 L 613 1133 L 614 1134 L 642 1134 L 643 1133 L 651 1133 L 652 1132 L 664 1131 L 665 1130 L 669 1130 L 670 1129 L 677 1128 L 681 1126 L 684 1126 L 685 1125 L 693 1123 L 696 1121 L 698 1121 L 701 1119 L 703 1119 L 718 1111 L 720 1109 L 730 1103 L 735 1098 L 736 1098 L 746 1087 L 746 1086 L 750 1081 L 751 1077 L 752 1076 L 754 1076 L 759 1083 L 764 1093 L 764 1095 L 766 1098 L 766 1101 L 767 1102 L 767 1106 L 768 1107 L 768 1122 L 767 1123 L 767 1127 L 766 1128 L 765 1132 L 768 1132 L 769 1131 L 773 1131 L 774 1130 L 785 1128 L 786 1127 L 797 1124 L 800 1122 L 802 1122 L 822 1112 L 824 1110 L 832 1106 L 835 1103 L 839 1101 L 847 1094 L 848 1094 L 864 1079 L 864 1078 L 870 1072 L 870 1071 L 877 1063 L 877 1062 L 883 1054 L 885 1049 L 887 1047 L 891 1039 L 891 1037 L 894 1032 L 894 1030 L 896 1027 L 896 1025 L 898 1021 L 898 1018 L 899 1017 L 899 1013 L 901 1011 L 903 1011 L 904 1012 L 904 1014 L 907 1021 L 907 1024 L 908 1025 L 908 1029 L 909 1030 L 910 1043 L 911 1044 L 911 1052 L 910 1053 L 910 1065 L 911 1065 L 921 1056 L 922 1056 L 940 1038 L 940 1037 L 944 1033 L 944 1032 L 950 1025 L 950 1024 L 958 1013 L 961 1006 L 963 1004 L 963 1002 L 970 988 L 970 986 L 971 985 L 971 983 L 974 976 L 976 965 L 977 964 L 978 951 L 979 950 L 979 914 L 978 913 L 978 906 L 977 905 L 976 895 L 977 894 L 979 894 L 984 899 L 984 900 L 987 903 L 987 904 L 993 912 L 1003 933 L 1003 935 L 1004 936 L 1004 938 L 1007 945 L 1007 948 L 1008 949 L 1008 954 L 1009 955 L 1009 960 L 1010 961 L 1011 961 L 1011 960 L 1017 952 L 1028 931 L 1028 929 L 1030 926 L 1031 921 L 1033 918 L 1033 915 L 1034 914 L 1035 908 L 1037 904 L 1037 900 L 1038 899 L 1038 895 L 1039 894 L 1039 889 L 1040 888 L 1040 880 L 1041 879 L 1041 853 L 1040 852 L 1040 841 L 1039 840 L 1039 835 L 1038 834 L 1037 824 L 1036 823 L 1035 816 L 1033 812 L 1033 809 L 1032 808 L 1029 797 L 1027 794 L 1027 792 L 1023 784 L 1023 782 L 1009 755 L 1000 742 L 999 738 L 1000 737 L 1002 737 L 1011 744 L 1012 744 L 1036 767 L 1036 768 L 1040 772 L 1044 779 L 1047 782 L 1056 799 L 1058 799 L 1058 796 L 1059 795 L 1059 792 L 1060 791 L 1060 788 L 1061 787 L 1061 783 L 1062 782 L 1062 778 L 1063 777 L 1063 770 L 1064 769 L 1064 759 L 1065 758 L 1065 742 L 1064 741 L 1064 729 L 1063 728 L 1062 715 L 1061 714 L 1060 704 L 1059 703 L 1059 700 L 1058 699 L 1058 696 L 1057 695 L 1055 684 L 1053 680 L 1053 677 L 1050 671 L 1050 668 L 1048 665 L 1047 660 L 1045 657 L 1045 655 L 1043 652 L 1041 645 L 1038 640 L 1038 638 L 1022 606 L 1020 604 L 1018 599 L 1016 597 L 1014 593 L 1014 591 L 1015 590 L 1017 590 L 1019 592 L 1032 600 L 1042 609 L 1043 609 L 1053 619 L 1053 620 L 1061 628 L 1061 629 L 1062 629 L 1062 598 L 1061 597 L 1061 590 L 1060 589 L 1060 584 L 1059 583 L 1059 578 L 1058 577 L 1058 574 L 1057 573 L 1057 569 L 1055 565 L 1055 562 L 1054 561 L 1053 555 L 1050 549 L 1049 544 L 1047 541 L 1045 534 L 1042 529 L 1042 527 L 1034 511 L 1032 509 L 1028 501 L 1026 499 L 1023 493 L 1016 484 L 1014 480 L 995 458 L 995 455 L 997 453 L 1001 445 L 1005 440 L 1006 437 L 1008 435 L 1014 425 L 1017 422 L 1019 418 L 1029 406 L 1029 405 L 1040 393 L 1043 388 L 1025 388 L 1024 389 L 1019 389 L 1018 390 L 1015 390 L 1008 393 L 1006 393 L 1005 392 L 1005 384 L 1004 383 L 1004 371 L 1003 370 L 1003 358 L 1002 357 L 1002 350 L 1001 349 L 1000 333 L 999 332 L 999 326 L 998 325 L 997 311 L 996 310 L 995 298 L 994 297 L 994 292 L 993 291 L 992 280 L 991 279 L 991 275 L 990 274 L 990 269 L 989 268 L 989 264 L 988 263 L 988 258 L 987 257 L 987 254 L 986 253 L 986 250 L 985 249 L 985 245 L 984 244 L 984 241 L 983 240 L 983 236 L 982 235 L 982 232 L 981 231 L 981 228 L 980 227 L 980 224 L 979 223 L 979 220 L 978 219 L 976 208 L 974 204 L 973 197 L 972 196 L 972 194 L 971 193 L 971 191 L 970 190 L 970 188 L 969 187 L 969 185 L 968 184 L 968 182 L 967 181 L 967 179 L 966 178 L 962 164 L 960 161 L 959 156 L 957 153 L 957 151 L 954 146 L 954 144 L 949 134 L 942 125 L 942 124 L 936 119 L 932 118 L 931 117 L 924 117 L 923 118 L 920 118 L 919 119 L 914 120 L 898 129 L 895 132 L 894 132 L 891 135 L 890 135 L 887 138 L 882 141 L 867 155 L 866 155 L 853 168 L 853 169 L 843 179 L 843 180 L 838 185 L 838 186 L 832 192 L 832 193 L 828 197 L 828 198 L 813 217 L 809 224 L 806 227 L 806 228 L 794 245 L 793 248 L 789 253 L 787 258 L 785 260 L 779 272 L 777 274 L 762 304 L 762 306 L 758 313 L 756 320 L 753 324 L 747 324 L 746 325 L 741 325 L 740 326 L 730 327 L 729 328 L 718 330 L 717 331 L 715 331 L 714 332 L 712 332 L 705 335 L 702 335 L 687 324 L 682 322 L 680 320 L 670 315 L 668 315 L 660 311 L 658 311 L 654 309 L 651 309 L 650 308 L 647 308 L 646 307 L 636 306 L 635 305 L 627 305 L 626 304 L 612 305 L 612 306 L 615 309 L 617 313 L 623 320 L 626 326 L 628 328 L 632 336 L 632 338 L 631 339 L 629 339 L 624 335 L 605 326 L 599 325 L 598 324 L 595 324 L 594 323 L 591 323 L 590 322 L 581 321 L 580 320 L 574 320 L 573 319 L 561 319 L 560 318 L 537 318 L 536 319 L 524 319 L 523 320 L 515 320 L 514 321 L 509 321 L 508 322 L 501 322 L 498 317 L 496 310 L 491 301 L 491 299 L 480 277 L 478 275 L 475 268 L 473 266 L 471 261 L 469 259 L 465 251 L 461 246 L 460 243 L 458 241 L 450 228 L 447 225 L 443 218 L 440 215 L 438 211 L 432 204 L 432 203 L 428 199 L 428 198 L 424 194 L 424 193 L 420 189 L 420 188 L 414 182 L 414 181 L 406 173 L 406 172 L 380 147 L 379 147 L 374 142 L 373 142 L 369 138 L 368 138 L 364 134 L 360 132 L 357 129 L 345 123 L 343 121 L 341 121 L 335 118 L 332 118 L 331 117 L 325 117 L 324 118 L 321 118 Z M 555 757 L 557 757 L 560 755 L 562 755 L 565 753 L 567 753 L 571 751 L 574 751 L 578 749 L 581 749 L 582 748 L 585 748 L 586 747 L 589 747 L 590 746 L 594 746 L 595 745 L 601 745 L 602 744 L 608 744 L 609 743 L 624 743 L 625 742 L 634 742 L 635 743 L 646 743 L 647 744 L 656 744 L 657 745 L 660 745 L 661 746 L 666 746 L 670 748 L 678 749 L 679 750 L 681 750 L 682 751 L 696 755 L 701 758 L 703 758 L 717 766 L 722 771 L 728 785 L 728 789 L 729 790 L 729 811 L 728 812 L 728 815 L 727 816 L 726 822 L 723 828 L 723 830 L 720 836 L 716 841 L 715 844 L 705 857 L 705 858 L 700 863 L 700 864 L 677 887 L 676 887 L 667 895 L 666 895 L 655 903 L 652 904 L 647 908 L 638 912 L 636 914 L 630 917 L 626 917 L 622 914 L 615 911 L 613 909 L 605 905 L 603 903 L 596 899 L 593 896 L 589 894 L 576 883 L 575 883 L 553 861 L 553 860 L 546 852 L 546 851 L 539 842 L 532 829 L 532 827 L 530 824 L 530 822 L 527 815 L 527 811 L 526 810 L 526 792 L 527 791 L 528 783 L 534 770 L 536 768 Z M 773 580 L 778 580 L 779 579 L 792 579 L 793 580 L 801 581 L 813 587 L 815 589 L 816 589 L 826 599 L 832 609 L 832 611 L 834 614 L 834 617 L 835 618 L 835 622 L 836 623 L 836 636 L 835 637 L 834 645 L 832 648 L 832 650 L 830 654 L 828 656 L 826 660 L 816 670 L 804 677 L 802 677 L 798 679 L 795 679 L 794 680 L 776 680 L 775 679 L 772 679 L 771 678 L 766 677 L 758 673 L 744 660 L 738 650 L 738 648 L 735 641 L 735 637 L 734 636 L 734 625 L 735 624 L 735 618 L 736 617 L 737 612 L 742 602 L 746 598 L 746 597 L 756 588 L 767 582 L 769 582 Z M 463 579 L 476 579 L 477 580 L 482 580 L 483 581 L 486 581 L 489 583 L 491 583 L 495 586 L 498 587 L 511 599 L 511 600 L 515 605 L 518 611 L 518 613 L 520 617 L 520 621 L 521 622 L 521 638 L 520 639 L 520 642 L 519 643 L 517 651 L 514 655 L 513 658 L 504 668 L 503 668 L 497 673 L 487 678 L 484 678 L 483 679 L 480 679 L 479 680 L 462 680 L 461 679 L 458 679 L 457 678 L 451 677 L 439 670 L 430 661 L 430 660 L 426 655 L 423 649 L 423 647 L 421 643 L 421 640 L 420 639 L 420 620 L 421 619 L 422 613 L 424 610 L 424 608 L 426 606 L 429 600 L 435 593 L 436 593 L 444 586 L 452 582 L 454 582 L 458 580 L 462 580 Z M 349 202 L 358 207 L 370 218 L 371 218 L 382 229 L 382 230 L 388 236 L 388 237 L 393 242 L 393 243 L 402 254 L 402 255 L 408 263 L 409 266 L 411 268 L 412 271 L 416 276 L 427 298 L 427 300 L 433 312 L 436 323 L 435 324 L 430 324 L 429 323 L 417 322 L 416 321 L 408 321 L 407 322 L 401 322 L 400 323 L 392 324 L 380 329 L 378 331 L 374 333 L 374 335 L 377 335 L 378 336 L 389 339 L 399 344 L 400 345 L 400 348 L 399 349 L 391 351 L 388 353 L 386 353 L 381 356 L 379 356 L 369 361 L 362 366 L 359 367 L 357 369 L 347 375 L 340 381 L 339 381 L 328 391 L 327 391 L 311 407 L 309 407 L 308 406 L 308 398 L 307 397 L 307 344 L 308 343 L 309 320 L 310 319 L 310 312 L 311 311 L 311 304 L 312 303 L 312 298 L 313 297 L 313 291 L 314 290 L 315 279 L 316 278 L 316 274 L 317 273 L 317 269 L 318 268 L 318 264 L 319 263 L 319 259 L 320 258 L 320 255 L 321 254 L 323 243 L 324 242 L 325 236 L 326 235 L 330 221 L 332 218 L 332 216 L 337 206 L 341 202 Z M 909 201 L 914 202 L 917 205 L 923 216 L 923 218 L 926 224 L 926 226 L 929 233 L 929 236 L 932 243 L 932 246 L 933 247 L 933 250 L 934 251 L 934 254 L 935 255 L 935 259 L 936 260 L 936 263 L 937 264 L 938 273 L 939 274 L 939 279 L 940 280 L 940 285 L 941 286 L 941 291 L 942 292 L 942 297 L 943 298 L 943 303 L 944 304 L 944 310 L 945 311 L 946 329 L 947 330 L 947 343 L 948 344 L 948 397 L 947 398 L 947 406 L 946 407 L 944 407 L 921 385 L 920 385 L 916 381 L 915 381 L 908 375 L 907 375 L 896 367 L 869 353 L 867 353 L 864 351 L 862 351 L 855 348 L 855 345 L 864 340 L 866 340 L 869 338 L 880 335 L 880 333 L 878 331 L 866 325 L 860 324 L 859 323 L 855 323 L 854 322 L 846 322 L 845 321 L 841 321 L 840 322 L 832 322 L 831 323 L 826 323 L 825 324 L 821 324 L 820 323 L 820 319 L 821 318 L 822 313 L 824 310 L 824 308 L 826 305 L 826 303 L 829 298 L 829 296 L 838 278 L 840 276 L 842 271 L 846 266 L 847 263 L 849 261 L 853 254 L 856 251 L 856 250 L 859 247 L 862 242 L 866 238 L 866 237 L 887 215 L 888 215 L 893 210 L 894 210 L 901 204 L 905 202 L 908 202 Z" />
      </svg>
    </span>
  );
}

/** Pequeño icono "i" con tooltip sutil al pasar el ratón o al enfocar con teclado. */
export function InfoHint({ text }: { text: string }) {
  return (
    <button
      type="button"
      aria-label={text}
      className="group relative inline-flex cursor-help items-center align-middle"
    >
      <span className="flex h-4 w-4 items-center justify-center rounded-full border border-line text-[10px] font-bold leading-none text-muted transition group-hover:border-accent group-hover:text-accent group-focus-visible:border-accent group-focus-visible:text-accent">
        i
      </span>
      <span className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 w-60 -translate-x-1/2 rounded-lg border border-line bg-panel2 px-3 py-2 text-xs font-normal normal-case leading-snug tracking-normal text-fg opacity-0 shadow-xl shadow-black/30 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100">
        {text}
      </span>
    </button>
  );
}

export function Kpi({
  label,
  value,
  sub,
  accent = false,
  info,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  info?: string;
}) {
  return (
    <Card className="flex flex-col gap-1">
      <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
        {label}
        {info && <InfoHint text={info} />}
      </span>
      <span className={`font-sans text-4xl font-bold tabular-nums ${accent ? "text-accent" : "text-fg"}`}>{value}</span>
      {sub && <span className="text-xs text-muted">{sub}</span>}
    </Card>
  );
}

export function SectionTitle({ children, info }: { children: React.ReactNode; info?: string }) {
  return (
    <h2 className="mb-3 mt-2 flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider text-muted">
      {children}
      {info && <InfoHint text={info} />}
    </h2>
  );
}

/** Control segmentado reutilizable (switch de varias opciones). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  variant = "box",
}: {
  options: { value: T; label: React.ReactNode; title?: string }[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel?: string;
  /** "box"/"range": pastilla con borde; activo con bg-accent text-white.
   *  "plain": sin recuadro; el activo se resalta con color. */
  variant?: "box" | "range" | "plain";
}) {
  const plain = variant === "plain";
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={plain ? "flex gap-1" : "flex gap-1 rounded-xl border border-line bg-panel/60 p-1"}
    >
      {options.map((o) => (
        <button
          key={o.value}
          title={o.title}
          onClick={() => onChange(o.value)}
          className={`rounded-lg text-sm font-medium transition ${plain ? "px-2.5 py-1" : "px-3 py-1.5"} ${
            value === o.value
              ? plain
                ? "bg-accent/15 text-accent"
                : "bg-accent text-white"
              : "text-muted hover:text-fg"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Cuadrado redondeado con icono teñido. Reutilizable en ListRow y headers. */
export function IconTile({
  accentRgb,
  children,
  className = "",
}: {
  accentRgb?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ${className}`}
      style={accentRgb ? { backgroundColor: `rgb(${accentRgb} / 0.13)` } : undefined}
    >
      {children}
    </span>
  );
}

/** Fila de lista: tile de icono + título/meta + valor en font-display. */
export function ListRow({
  icon,
  title,
  meta,
  value,
  accentRgb,
  onClick,
}: {
  icon?: React.ReactNode;
  title: string;
  meta?: string;
  value?: string;
  accentRgb?: string;
  onClick?: () => void;
}) {
  const Tag = (onClick ? "button" : "div") as React.ElementType;
  return (
    <Tag
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${onClick ? "hover:bg-panel2/60" : ""}`}
    >
      {icon != null && <IconTile accentRgb={accentRgb}>{icon}</IconTile>}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-fg">{title}</span>
        {meta && <span className="truncate text-xs text-muted">{meta}</span>}
      </div>
      {value && (
        <span className="shrink-0 font-display text-base font-semibold text-fg">{value}</span>
      )}
    </Tag>
  );
}

/** Botón de item principal del sidebar. */
export function NavItemButton({
  icon,
  label,
  active = false,
  badge,
  collapsed = false,
  accentRgb,
  hasChildren = false,
  expanded = false,
  onClick,
}: {
  icon: string;
  label: string;
  active?: boolean;
  badge?: number;
  collapsed?: boolean;
  accentRgb?: string;
  hasChildren?: boolean;
  expanded?: boolean;
  onClick?: () => void;
}) {
  const activeStyle: React.CSSProperties =
    active && accentRgb
      ? { backgroundColor: `rgb(${accentRgb} / 0.12)`, color: `rgb(${accentRgb})` }
      : {};
  const iconColor = active && accentRgb ? `rgb(${accentRgb})` : undefined;
  return (
    <button
      onClick={onClick}
      title={collapsed ? label : undefined}
      aria-current={active ? "page" : undefined}
      style={activeStyle}
      className={`group relative flex w-full items-center gap-3 rounded-[11px] px-2.5 py-2 text-[13.5px] font-semibold transition-colors ${collapsed ? "justify-center" : ""} ${
        active ? "" : "text-muted hover:text-fg"
      }`}
    >
      <span className="relative flex shrink-0 items-center justify-center">
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5"
          fill="none"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ stroke: iconColor ?? "currentColor" }}
          aria-hidden
        >
          <path d={icon} />
        </svg>
        {badge != null && badge > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[9px] font-bold text-white">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </span>
      {!collapsed && (
        <>
          <span className="flex-1 truncate text-left">{label}</span>
          {hasChildren && (
            <svg
              viewBox="0 0 24 24"
              className={`h-3.5 w-3.5 shrink-0 stroke-current transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          )}
        </>
      )}
    </button>
  );
}

/** Sub-item indentado bajo un NavItemButton. */
export function NavSubButton({
  label,
  active = false,
  accentRgb,
  onClick,
}: {
  label: string;
  active?: boolean;
  accentRgb?: string;
  onClick?: () => void;
}) {
  const activeStyle: React.CSSProperties =
    active && accentRgb
      ? { backgroundColor: `rgb(${accentRgb} / 0.10)`, color: `rgb(${accentRgb})` }
      : {};
  return (
    <button
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      style={activeStyle}
      className={`flex w-full items-center gap-2.5 rounded-[9px] px-3 py-1.5 text-[12.5px] font-medium transition-colors ${
        active ? "" : "text-muted hover:text-fg"
      }`}
    >
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: accentRgb ? `rgb(${accentRgb})` : "rgb(var(--accent))" }}
      />
      <span className="truncate">{label}</span>
    </button>
  );
}

/** Avatar con iniciales (o foto) + nombre + rol. Pie del sidebar. */
export function ProfileChip({
  name,
  role,
  avatar,
  collapsed = false,
}: {
  name: string;
  role: string;
  avatar?: string;
  collapsed?: boolean;
}) {
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <div className="flex items-center gap-2.5 overflow-hidden">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-white">
        {avatar ? (
          <img src={avatar} alt={name} className="h-full w-full rounded-full object-cover" />
        ) : (
          initials
        )}
      </span>
      {!collapsed && (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-[13px] font-semibold text-fg">{name}</span>
          <span className="truncate text-[11px] text-muted">{role}</span>
        </div>
      )}
    </div>
  );
}

/** Burbuja de chat para Coach IA. role="ai" usa acento; role="user" usa panel. */
export function ChatBubble({
  role,
  children,
  avatar,
  name,
  accentRgb,
}: {
  role: "ai" | "user";
  children: React.ReactNode;
  avatar?: React.ReactNode;
  name?: string;
  accentRgb?: string;
}) {
  const isAi = role === "ai";
  const bubbleStyle: React.CSSProperties =
    isAi && accentRgb
      ? {
          borderColor: `rgb(${accentRgb} / 0.25)`,
          backgroundColor: `rgb(${accentRgb} / 0.10)`,
        }
      : {};
  return (
    <div className={`flex gap-2.5 ${isAi ? "" : "flex-row-reverse"}`}>
      {avatar != null && (
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center">{avatar}</div>
      )}
      <div className={`flex max-w-[80%] flex-col gap-1 ${isAi ? "items-start" : "items-end"}`}>
        {name && <span className="px-1 text-[11px] text-muted">{name}</span>}
        <div
          className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed border ${
            isAi ? "rounded-tl-[4px] border-line bg-panel text-fg" : "border-line bg-panel2 text-fg"
          }`}
          style={isAi ? bubbleStyle : undefined}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/** Selector de mes/año: botón-etiqueta del mes que abre un popover con rejilla 12 meses +
 *  stepper de año + botón "Hoy". Se cierra con Escape o click fuera. */
export function MonthPicker({
  value,
  onChange,
  todayLabel,
  openLabel,
  lang,
}: {
  value: Date;
  onChange: (d: Date) => void;
  todayLabel: string;
  openLabel: string;
  lang: string;
}) {
  const [open, setOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(value.getFullYear());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const locale = lang === "es" ? "es-ES" : "en-US";
  const rawLabel = value.toLocaleDateString(locale, { month: "long", year: "numeric" });
  const monthLabel = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
  const monthNames = Array.from({ length: 12 }, (_, i) =>
    new Date(2024, i, 1).toLocaleString(locale, { month: "short" })
  );
  const currentMonth = value.getMonth();
  const currentYear = value.getFullYear();

  const selectMonth = (m: number) => {
    onChange(new Date(pickerYear, m, 1));
    setOpen(false);
  };

  const goToday = () => {
    const now = new Date();
    setPickerYear(now.getFullYear());
    onChange(new Date(now.getFullYear(), now.getMonth(), 1));
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setPickerYear(value.getFullYear()); setOpen((o) => !o); }}
        title={openLabel}
        className="rounded-lg px-2 py-1 text-sm font-semibold capitalize text-fg transition hover:bg-accent/10 hover:text-accent"
      >
        {monthLabel}
      </button>
      {open && (
        <div className="absolute left-1/2 top-full z-50 mt-1 w-52 -translate-x-1/2 rounded-xl border border-line bg-panel shadow-xl">
          {/* Stepper de año */}
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <button
              onClick={() => setPickerYear((y) => y - 1)}
              aria-label={String(pickerYear - 1)}
              className="rounded p-1 text-muted transition hover:text-fg"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <span className="text-sm font-semibold">{pickerYear}</span>
            <button
              onClick={() => setPickerYear((y) => y + 1)}
              aria-label={String(pickerYear + 1)}
              className="rounded p-1 text-muted transition hover:text-fg"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
          {/* Rejilla de 12 meses */}
          <div className="grid grid-cols-3 gap-1 p-2">
            {monthNames.map((name, i) => {
              const isSelected = i === currentMonth && pickerYear === currentYear;
              return (
                <button
                  key={i}
                  onClick={() => selectMonth(i)}
                  className={`rounded-lg py-1.5 text-xs font-medium capitalize transition ${
                    isSelected
                      ? "bg-accent text-white"
                      : "text-muted hover:bg-accent/10 hover:text-fg"
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
          {/* Botón Hoy */}
          <div className="border-t border-line p-2">
            <button
              onClick={goToday}
              className="w-full rounded-lg py-1.5 text-xs font-semibold text-accent hover:bg-accent/10"
            >
              {todayLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
